import { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { IoClose } from 'react-icons/io5';
import Pagination from '../../../components/common/Pagination';
import RichEditor, { sanitize } from '../../../components/common/RichEditor';
import { getAllNotices, createNotice, updateNotice, deleteNotice } from '../../../services/noticeService';

const PER_PAGE = 20;

const emptyForm = { category: '', title: '', content: '', isPublished: true };

const NoticeList = () => {
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(1);

  const fetchData = async () => {
    setLoading(true);
    try {
      const data = await getAllNotices();
      setNotices(data);
    } catch {
      setNotices([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const totalPages = Math.max(1, Math.ceil(notices.length / PER_PAGE));
  const paged = useMemo(() => notices.slice((page - 1) * PER_PAGE, page * PER_PAGE), [notices, page]);

  const detail = useMemo(() => notices.find((n) => n.id === detailId), [notices, detailId]);

  const openCreate = () => {
    setEditTarget(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (notice) => {
    setEditTarget(notice);
    setForm({
      category: notice.category || '',
      title: notice.title || '',
      content: notice.content || '',
      isPublished: notice.isPublished !== false,
    });
    setDetailId(null);
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!form.title || !form.content) {
      alert('제목과 내용을 입력해 주세요.');
      return;
    }
    try {
      if (editTarget) {
        await updateNotice(editTarget.id, {
          category: form.category || '',
          title: form.title,
          content: form.content,
          isPublished: form.isPublished,
        });
      } else {
        await createNotice({
          category: form.category || '',
          title: form.title,
          content: form.content,
          isPublished: form.isPublished,
        });
      }
      setShowModal(false);
      setEditTarget(null);
      setForm(emptyForm);
      await fetchData();
    } catch (err) {
      alert('저장 실패: ' + err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('삭제하시겠습니까?')) return;
    try {
      await deleteNotice(id);
      setDetailId(null);
      setSelected((prev) => prev.filter((x) => x !== id));
      await fetchData();
    } catch (err) {
      alert('삭제 실패: ' + err.message);
    }
  };

  const toggleOne = (id) => {
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const allPagedSelected = paged.length > 0 && paged.every((n) => selected.includes(n.id));
  const toggleAll = () => {
    if (allPagedSelected) {
      setSelected((prev) => prev.filter((id) => !paged.some((n) => n.id === id)));
    } else {
      setSelected((prev) => [...new Set([...prev, ...paged.map((n) => n.id)])]);
    }
  };

  const bulkPublish = async (isPublished) => {
    if (selected.length === 0) return;
    try {
      await Promise.all(selected.map((id) => updateNotice(id, { isPublished })));
      setSelected([]);
      await fetchData();
    } catch (err) {
      alert('일괄 변경 실패: ' + err.message);
    }
  };

  const bulkDelete = async () => {
    if (selected.length === 0) return;
    if (!window.confirm(`선택한 ${selected.length}건을 삭제하시겠습니까?`)) return;
    try {
      await Promise.all(selected.map((id) => deleteNotice(id)));
      setSelected([]);
      await fetchData();
    } catch (err) {
      alert('일괄 삭제 실패: ' + err.message);
    }
  };

  const togglePublish = async (notice) => {
    try {
      await updateNotice(notice.id, { isPublished: notice.isPublished === false });
      await fetchData();
    } catch (err) {
      alert('변경 실패: ' + err.message);
    }
  };

  return (
    <Container>
      <Header>
        <TitleBox>
          <Title>공지사항</Title>
          <Location>노출 위치: 랜딩 푸터 · 앱 초대 · 마이페이지</Location>
        </TitleBox>
        <HeaderActions>
          <TextBtn onClick={fetchData}>새로고침</TextBtn>
          <PrimaryBtn onClick={openCreate}>공지 등록</PrimaryBtn>
        </HeaderActions>
      </Header>

      <ActionBar>
        <LeftActions>
          <CheckLabel>
            <Check type="checkbox" checked={allPagedSelected} onChange={toggleAll} />
            전체선택 ({paged.length}건)
          </CheckLabel>
          {selected.length > 0 && (
            <>
              <SelCount>{selected.length}건 선택됨</SelCount>
              <TextBtn onClick={() => setSelected([])}>해제</TextBtn>
            </>
          )}
        </LeftActions>
        <RightActions>
          <TextBtn disabled={selected.length === 0} onClick={() => bulkPublish(true)}>일괄 공개</TextBtn>
          <TextBtn disabled={selected.length === 0} onClick={() => bulkPublish(false)}>일괄 비공개</TextBtn>
          <DangerText disabled={selected.length === 0} onClick={bulkDelete}>일괄 삭제</DangerText>
        </RightActions>
      </ActionBar>

      <TableWrap>
        <Table>
          <thead>
            <tr>
              <Th style={{ width: 40 }}></Th>
              <Th style={{ width: 140 }}>카테고리</Th>
              <Th>제목</Th>
              <Th style={{ width: 110 }}>공개여부</Th>
              <Th style={{ width: 120 }}>등록일</Th>
              <Th style={{ width: 180 }}>관리</Th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><Td colSpan={6} style={{ textAlign: 'center', padding: '40px 0' }}>로딩 중...</Td></tr>
            ) : paged.length === 0 ? (
              <tr><Td colSpan={6} style={{ textAlign: 'center', padding: '40px 0', color: '#9CA3AF' }}>등록된 공지가 없습니다.</Td></tr>
            ) : paged.map((n) => {
              const published = n.isPublished !== false;
              return (
                <tr key={n.id}>
                  <Td><Check type="checkbox" checked={selected.includes(n.id)} onChange={() => toggleOne(n.id)} /></Td>
                  <Td>{n.category || '-'}</Td>
                  <Td>
                    <TitleLink onClick={() => setDetailId(n.id)}>{n.title}</TitleLink>
                  </Td>
                  <Td>
                    <PubState $on={published} onClick={() => togglePublish(n)}>
                      {published ? '공개' : '비공개'}
                    </PubState>
                  </Td>
                  <Td style={{ color: '#6B7280' }}>{n.date}</Td>
                  <Td>
                    <InlineActions>
                      <TextBtn onClick={() => setDetailId(n.id)}>보기</TextBtn>
                      <TextBtn onClick={() => openEdit(n)}>편집</TextBtn>
                      <DangerText onClick={() => handleDelete(n.id)}>삭제</DangerText>
                    </InlineActions>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </Table>
        <Pagination current={page} total={totalPages} onChange={setPage} />
      </TableWrap>

      {showModal && (
        <Overlay onClick={() => setShowModal(false)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{editTarget ? '공지 수정' : '공지 등록'}</ModalTitle>
              <CloseBtn onClick={() => setShowModal(false)}><IoClose size={22} /></CloseBtn>
            </ModalHeader>
            <ModalBody>
              <Row>
                <Field $flex={1}>
                  <FieldLabel>카테고리</FieldLabel>
                  <Input
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    placeholder="예: 이벤트, 점검, 업데이트"
                  />
                </Field>
              </Row>
              <Field>
                <FieldLabel>제목</FieldLabel>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="공지 제목"
                />
              </Field>
              <Field>
                <FieldLabel>내용</FieldLabel>
                <RichEditor
                  value={form.content}
                  onChange={(html) => setForm({ ...form, content: html })}
                  placeholder="공지 내용을 입력하세요"
                  minHeight={220}
                />
              </Field>
              <CheckRow>
                <Check
                  type="checkbox"
                  id="notice-published"
                  checked={form.isPublished}
                  onChange={(e) => setForm({ ...form, isPublished: e.target.checked })}
                />
                <label htmlFor="notice-published">
                  <strong>공개</strong>
                  <span> — 체크 해제 시 고객 앱/마이페이지에 표시되지 않습니다.</span>
                </label>
              </CheckRow>
            </ModalBody>
            <ModalFooter>
              <TextBtn onClick={() => setShowModal(false)}>취소</TextBtn>
              <PrimaryBtn onClick={handleSubmit}>{editTarget ? '저장' : '등록'}</PrimaryBtn>
            </ModalFooter>
          </Modal>
        </Overlay>
      )}

      {detail && (
        <Overlay onClick={() => setDetailId(null)}>
          <DetailModal onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{detail.title}</ModalTitle>
              <CloseBtn onClick={() => setDetailId(null)}><IoClose size={22} /></CloseBtn>
            </ModalHeader>
            <ModalBody>
              <MetaRow>
                <span>{detail.category || '-'}</span>
                <span>{detail.date}</span>
                <span>{detail.isPublished === false ? '비공개' : '공개'}</span>
              </MetaRow>
              <ContentArea dangerouslySetInnerHTML={{ __html: sanitize(detail.content || '') }} />
            </ModalBody>
            <ModalFooter>
              <DangerText onClick={() => handleDelete(detail.id)}>삭제</DangerText>
              <PrimaryBtn onClick={() => openEdit(detail)}>수정</PrimaryBtn>
            </ModalFooter>
          </DetailModal>
        </Overlay>
      )}
    </Container>
  );
};

export default NoticeList;

const Container = styled.div``;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  gap: 16px;
  flex-wrap: wrap;
`;

const TitleBox = styled.div``;

const Title = styled.h1`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 4px;
`;

const Location = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const TextBtn = styled.button`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
  background: none;
  padding: 4px 0;
  &:hover:not(:disabled) { color: ${({ theme }) => theme.colors.primary}; text-decoration: underline; }
  &:disabled { color: ${({ theme }) => theme.colors.textLight}; cursor: default; }
`;

const DangerText = styled(TextBtn)`
  color: ${({ theme }) => theme.colors.danger};
  &:hover:not(:disabled) { color: ${({ theme }) => theme.colors.danger}; text-decoration: underline; }
  &:disabled { color: ${({ theme }) => theme.colors.textLight}; }
`;

const PrimaryBtn = styled.button`
  padding: 8px 18px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 13px;
  font-weight: 500;
`;

const ActionBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  background: #F9FAFB;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  margin-bottom: 12px;
  flex-wrap: wrap;
  gap: 12px;
`;

const LeftActions = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

const RightActions = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

const CheckLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
`;

const Check = styled.input`
  width: 14px;
  height: 14px;
  cursor: pointer;
`;

const SelCount = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text};
  font-weight: 500;
`;

const TableWrap = styled.div`
  background: ${({ theme }) => theme.colors.bgWhite};
  border-radius: ${({ theme }) => theme.radius.md};
  box-shadow: ${({ theme }) => theme.shadow.sm};
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px 16px;
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textSecondary};
  border-bottom: 2px solid ${({ theme }) => theme.colors.border};
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 12px 16px;
  font-size: 13px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  vertical-align: middle;
`;

const TitleLink = styled.button`
  background: none;
  padding: 0;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text};
  text-align: left;
  &:hover { color: ${({ theme }) => theme.colors.primary}; text-decoration: underline; }
`;

const PubState = styled.button`
  background: none;
  padding: 0;
  font-size: 13px;
  font-weight: 500;
  color: ${({ $on, theme }) => ($on ? theme.colors.primary : theme.colors.textLight)};
  &:hover { text-decoration: underline; }
`;

const InlineActions = styled.div`
  display: flex;
  gap: 12px;
`;

const Overlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const Modal = styled.div`
  background: #fff;
  border-radius: ${({ theme }) => theme.radius.lg};
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
`;

const DetailModal = styled(Modal)`
  max-width: 640px;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 24px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const ModalTitle = styled.h2`
  font-size: 16px;
  font-weight: 600;
`;

const CloseBtn = styled.button`
  color: ${({ theme }) => theme.colors.textLight};
`;

const ModalBody = styled.div`
  padding: 20px 24px;
  overflow-y: auto;
  flex: 1;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  align-items: center;
  padding: 14px 24px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const Row = styled.div`
  display: flex;
  gap: 12px;
`;

const Field = styled.div`
  flex: ${({ $flex }) => $flex || 1};
  margin-bottom: 16px;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 13px;
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.primary}; }
`;

const CheckRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
  label { cursor: pointer; }
  strong { color: ${({ theme }) => theme.colors.text}; font-weight: 500; }
`;

const MetaRow = styled.div`
  display: flex;
  gap: 16px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const ContentArea = styled.div`
  font-size: 13px;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.text};
  word-break: break-word;
`;
